import { Component, OnInit, ViewChild } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { UserDetailsDto, UserDetailStatus } from "@models/user.model";
import { UserService } from "../services/user/user.service";
import { SnackBarConfig } from "../constants/snackbar.constants";
import { RouteTrackingService } from "../services/route-tracking/route-tracking.service";
import { CommonModule } from "@angular/common";
import { FormGroup, NonNullableFormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelect, MatSelectModule } from "@angular/material/select";
import { nameValidator } from "../shared/name-validator.directive";
import { trimmedRequired } from "../shared/validators/trimmed-required.validator";
import { trimFormValues } from "../shared/utils/trim-form-values.util";
import { digitsOnly } from "../shared/validators/digits-only.validator";
import { TrimOnBlurDirective } from "../shared/directives/trim-on-blur.directive";
import { indianPincode } from "../shared/validators/indian-pincode.validator";
import { indianPhoneNumber } from "../shared/validators/indian-phone-number.validator";
import { FirstErrorPipe } from "../shared/pipes/first-error.pipe";

interface State {
    id: number;
    value: string;
    viewValue: string;
}

enum AddressType {
    Home = "home",
    Work = "work",
    Other = "other",
}

interface AddressTypeOption {
    type: AddressType;
    label: string;
    icon: string;
}

@Component({
    selector: "app-user-details",
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatCheckboxModule,
        MatButtonModule,
        MatIconModule,
        TrimOnBlurDirective,
        FirstErrorPipe,
    ],
    templateUrl: "./user-details.component.html",
    styleUrls: ["./user-details.component.scss"],
})
export class UserDetailsComponent implements OnInit {
    @ViewChild("stateSelect") stateSelect!: MatSelect;
    @ViewChild("recipientStateSelect") recipientStateSelect!: MatSelect;

    public indianStates: State[] = [
        // States
        { id: 1, value: "Andhra Pradesh", viewValue: "Andhra Pradesh" },
        { id: 2, value: "Arunachal Pradesh", viewValue: "Arunachal Pradesh" },
        { id: 3, value: "Assam", viewValue: "Assam" },
        { id: 4, value: "Bihar", viewValue: "Bihar" },
        { id: 5, value: "Chhattisgarh", viewValue: "Chhattisgarh" },
        { id: 6, value: "Goa", viewValue: "Goa" },
        { id: 7, value: "Gujarat", viewValue: "Gujarat" },
        { id: 8, value: "Haryana", viewValue: "Haryana" },
        { id: 9, value: "Himachal Pradesh", viewValue: "Himachal Pradesh" },
        { id: 10, value: "Jharkhand", viewValue: "Jharkhand" },
        { id: 11, value: "Karnataka", viewValue: "Karnataka" },
        { id: 12, value: "Kerala", viewValue: "Kerala" },
        { id: 13, value: "Madhya Pradesh", viewValue: "Madhya Pradesh" },
        { id: 14, value: "Maharashtra", viewValue: "Maharashtra" },
        { id: 15, value: "Manipur", viewValue: "Manipur" },
        { id: 16, value: "Meghalaya", viewValue: "Meghalaya" },
        { id: 17, value: "Mizoram", viewValue: "Mizoram" },
        { id: 18, value: "Nagaland", viewValue: "Nagaland" },
        { id: 19, value: "Odisha", viewValue: "Odisha" },
        { id: 20, value: "Punjab", viewValue: "Punjab" },
        { id: 21, value: "Rajasthan", viewValue: "Rajasthan" },
        { id: 22, value: "Sikkim", viewValue: "Sikkim" },
        { id: 23, value: "Tamil Nadu", viewValue: "Tamil Nadu" },
        { id: 24, value: "Telangana", viewValue: "Telangana" },
        { id: 25, value: "Tripura", viewValue: "Tripura" },
        { id: 26, value: "Uttar Pradesh", viewValue: "Uttar Pradesh" },
        { id: 27, value: "Uttarakhand", viewValue: "Uttarakhand" },
        { id: 28, value: "West Bengal", viewValue: "West Bengal" },

        // Union Territories
        { id: 29, value: "Andaman and Nicobar Islands", viewValue: "Andaman and Nicobar Islands" },
        { id: 30, value: "Chandigarh", viewValue: "Chandigarh" },
        {
            id: 31,
            value: "Dadra and Nagar Haveli and Daman and Diu",
            viewValue: "Dadra and Nagar Haveli and Daman and Diu",
        },
        { id: 32, value: "Delhi", viewValue: "Delhi" },
        { id: 33, value: "Jammu and Kashmir", viewValue: "Jammu and Kashmir" },
        { id: 34, value: "Ladakh", viewValue: "Ladakh" },
        { id: 35, value: "Lakshadweep", viewValue: "Lakshadweep" },
        { id: 36, value: "Puducherry", viewValue: "Puducherry" },
    ];

    public AddressType = AddressType;

    public addressTypeOptions: AddressTypeOption[] = [
        {
            type: AddressType.Home,
            label: "Home",
            icon: "home",
        },
        {
            type: AddressType.Work,
            label: "Work",
            icon: "briefcase",
        },
        {
            type: AddressType.Other,
            label: "Other",
            icon: "location",
        },
    ];

    private recipientValidators = {
        name: [trimmedRequired(), nameValidator()],
        mobileNumber: [trimmedRequired(), indianPhoneNumber()],
        houseNumber: [trimmedRequired()],
        pincode: [trimmedRequired(), indianPincode()],
        address: [trimmedRequired()],
        city: [trimmedRequired()],
        state: [trimmedRequired()],
        addressType: [trimmedRequired()],
    };

    public detailsForm!: FormGroup;

    constructor(
        private userService: UserService,
        private snackBar: MatSnackBar,
        private router: Router,
        private routerService: RouteTrackingService,
        private fb: NonNullableFormBuilder,
    ) {}

    ngOnInit(): void {
        this.buildForm();

        this.detailsForm
            .get("useDifferentRecipientInfo")!
            .valueChanges.subscribe((enabled) => this.toggleRecipientValidators(enabled));
    }

    private buildForm(): void {
        this.detailsForm = this.fb.group({
            name: ["", [trimmedRequired(), nameValidator()]],
            email: ["", [trimmedRequired(), Validators.email]],
            age: ["", [trimmedRequired(), digitsOnly()]],
            houseNumber: ["", [trimmedRequired()]],
            pincode: ["", [trimmedRequired(), indianPincode()]],
            address: ["", [trimmedRequired()]],
            city: ["", [trimmedRequired()]],
            state: ["", [trimmedRequired()]],
            addressType: [AddressType.Home, [trimmedRequired()]],
            isDefault: [true],

            useDifferentRecipientInfo: [false],

            recipient: this.fb.group({
                name: [""],
                mobileNumber: [""],
                houseNumber: [""],
                pincode: [""],
                address: [""],
                city: [""],
                state: [""],
                addressType: [AddressType.Home],
                isDefault: [false],
            }),
        });
    }

    private toggleRecipientValidators(enabled: boolean): void {
        const group = this.detailsForm.get("recipient") as FormGroup;
        Object.entries(group.controls).forEach(([key, control]) => {
            enabled
                ? control.setValidators(this.recipientValidators[key as keyof typeof this.recipientValidators] ?? [])
                : control.clearValidators();

            control.updateValueAndValidity({ emitEvent: false });
        });

        if (!enabled) {
            group.reset({
                name: "",
                mobileNumber: "",
                houseNumber: "",
                pincode: "",
                address: "",
                city: "",
                state: "",
                addressType: AddressType.Home,
                isDefault: false,
            });
        }
    }

    public allowOnlyNumbers(event: KeyboardEvent): void {
        const allowedKeys = ["Backspace", "ArrowLeft", "ArrowRight", "Tab", "Delete"];

        if (allowedKeys.includes(event.key)) {
            return;
        }

        if (!/^\d$/.test(event.key)) {
            event.preventDefault();
        }
    }

    public openStateSelect(isRecipient: boolean = false): void {
        if (isRecipient) {
            this.recipientStateSelect.open();
        } else {
            this.stateSelect.open();
        }
    }

    public trackByStateId(_: number, state: State): number {
        return state.id;
    }

    private openSnackBar(message: string, action: string) {
        this.snackBar.open(message, action, { duration: SnackBarConfig.Duration.MEDIUM });
    }

    public onSkip(): void {
        this.navigateBack();
    }

    public navigateBack(): void {
        const navigatePreviousUrl = this.routerService.getPreviousUrl(); // To navigate the previous route
        this.router.navigate([navigatePreviousUrl]);
    }

    public onSubmit(): void {
        if (this.detailsForm.invalid) {
            this.detailsForm.markAllAsTouched();
            return;
        }

        const rawValue = this.detailsForm.value;
        const value = trimFormValues(rawValue);

        const payload: UserDetailsDto = {
            userDetails: {
                name: value.name,
                email: value.email,
                age: Number(value.age),
            },
            addressDetails: {
                houseNumber: value.houseNumber,
                pincode: Number(value.pincode),
                address: value.address,
                city: value.city,
                state: value.state,
                addressType: value.addressType,
                isDefault: value.isDefault,
            },
            recipient: value.useDifferentRecipientInfo ? { ...value.recipient } : null,
        };

        this.userService.addUserDetails(payload).subscribe(() => {
            this.openSnackBar(UserDetailStatus.Add, SnackBarConfig.Actions.CLOSE);
            this.navigateBack();
        });
    }
}
